import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition, Combobox } from '@headlessui/react';
import { XMarkIcon, CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useDateFormatter } from '../hooks/useDateFormatter';
import toast from 'react-hot-toast';
import { getLocalDateString } from '../utils/dateHelpers';
import DateTimePickerInput from './DateTimePickerInput';
import {
  getTimeSpentValidationError,
  TIME_SPENT_INCREMENT_MINUTES,
} from '../utils/timeEntryTimeSpent';

interface TypeOption {
  value: string;
  label: string;
}

interface GroupOption {
  id: string;
  name: string;
}

interface FieldErrors {
  type?: string;
  groupId?: string;
  timeSpent?: string;
  workedDate?: string;
  description?: string;
}

interface RecordTimeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  // Pre-populated readonly fields
  customerName: string;
  customerId: string;
  engagementName: string;
  engagementId: string;
  groupName: string;
  groupId: string;
  ticketNumber: string; // Ticket/Task number to pre-fill
}

const RecordTimeModal: React.FC<RecordTimeModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  customerName,
  customerId,
  engagementName,
  engagementId,
  groupName: _groupName,
  groupId,
  ticketNumber
}) => {
  const { user } = useAuth();
  const { getToday, formatDateForInput } = useDateFormatter();
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [typeOptions, setTypeOptions] = useState<TypeOption[]>([]);
  const [groupOptions, setGroupOptions] = useState<GroupOption[]>([]);
  const [typeQuery, setTypeQuery] = useState('');
  const [groupQuery, setGroupQuery] = useState('');

  const [formData, setFormData] = useState({
    type: '',
    selectedGroupId: groupId,
    timeSpent: '',
    workedDate: getLocalDateString(),
    description: ''
  });

  useEffect(() => {
    if (isOpen) {
      if (engagementId) {
        fetchTypeOptions(engagementId);
      } else {
        setTypeOptions([]);
      }
      fetchGroupOptions();
      // Reset form when modal opens
      setFormData({
        type: '',
        selectedGroupId: groupId,
        timeSpent: '',
        workedDate: getLocalDateString(),
        description: ''
      });
      setFieldErrors({});
      setTypeQuery('');
      setGroupQuery('');
    }
  }, [isOpen, groupId, engagementId]);

  const fetchTypeOptions = async (engagementId: string) => {
    try {
      const response = await api.get(`/time-entries/types?engagementId=${engagementId}`);
      setTypeOptions(response.data || []);
    } catch (error) {
      console.error('Error fetching type options:', error);
      toast.error('Failed to load time entry types');
    }
  };

  const fetchGroupOptions = async () => {
    if (!engagementId) return;
    try {
      const response = await api.get(`/time-entries/user-groups?engagementId=${engagementId}`);
      setGroupOptions(response.data || []);
    } catch (error) {
      console.error('Error fetching group options:', error);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (fieldErrors[field as keyof FieldErrors]) {
      setFieldErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const filteredTypes = typeQuery === ''
    ? typeOptions
    : typeOptions.filter((option) =>
        option.label.toLowerCase().includes(typeQuery.toLowerCase())
      );

  const filteredGroups = groupQuery === ''
    ? groupOptions
    : groupOptions.filter((option) =>
        option.name.toLowerCase().includes(groupQuery.toLowerCase())
      );

  const selectedType = typeOptions.find(t => t.value === formData.type);
  const selectedGroup = groupOptions.find(g => g.id === formData.selectedGroupId);

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};

    if (!formData.type) {
      errors.type = 'Type is required';
    }
    const timeSpentError = getTimeSpentValidationError(formData.timeSpent);
    if (timeSpentError) {
      errors.timeSpent = timeSpentError;
    }
    if (!formData.workedDate) {
      errors.workedDate = 'Worked Date is required';
    } else {
      // Compare dates as YYYY-MM-DD strings in user's timezone
      const todayStr = getToday(); // YYYY-MM-DD in user's timezone
      if (formData.workedDate > todayStr) {
        errors.workedDate = 'Worked Date cannot be in the future';
      }
    }
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      await api.post('/time-entries', {
        customerId,
        engagementId,
        groupId: formData.selectedGroupId || undefined,
        type: formData.type,
        timeSpent: parseInt(formData.timeSpent),
        workedDate: formData.workedDate,
        description: formData.description,
        task: ticketNumber
      });

      toast.success('Time entry recorded successfully');
      handleClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Error creating time entry:', error);
      const errorMessage = error.response?.data?.message || 'Failed to record time entry';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({
      type: '',
      selectedGroupId: groupId,
      timeSpent: '',
      workedDate: getLocalDateString(),
      description: ''
    });
    setFieldErrors({});
    setTypeQuery('');
    setGroupQuery('');
    onClose();
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    onClick={handleClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:ml-0 sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                      Record Time
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Record time spent on this ticket
                      </p>
                    </div>

                    <div className="mt-6 space-y-6">
                      {/* Row 1: User, Customer - 2 columns (readonly) */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                        <div className="grid grid-cols-3 gap-4 items-start">
                          <label className="text-sm font-medium text-gray-700 pt-2">User</label>
                          <div className="col-span-2">
                            <input
                              type="text"
                              value={`${user?.firstName || ''} ${user?.lastName || ''}`}
                              disabled
                              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4 items-start">
                          <label className="text-sm font-medium text-gray-700 pt-2">Customer</label>
                          <div className="col-span-2">
                            <input
                              type="text"
                              value={customerName}
                              disabled
                              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Row 2: Task, Engagement - 2 columns (readonly) */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                        <div className="grid grid-cols-3 gap-4 items-start">
                          <label className="text-sm font-medium text-gray-700 pt-2">Task</label>
                          <div className="col-span-2">
                            <input
                              type="text"
                              value={ticketNumber}
                              disabled
                              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4 items-start">
                          <label className="text-sm font-medium text-gray-700 pt-2">Engagement</label>
                          <div className="col-span-2">
                            <input
                              type="text"
                              value={engagementName}
                              disabled
                              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Row 3: Group, Type - 2 columns */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                        <div className="grid grid-cols-3 gap-4 items-start">
                          <label className="text-sm font-medium text-gray-700 pt-2">Group</label>
                          <div className="col-span-2">
                            <Combobox
                              value={formData.selectedGroupId}
                              onChange={(value) => handleInputChange('selectedGroupId', value)}
                            >
                              <div className="relative">
                                <div className="relative w-full">
                                  <Combobox.Input
                                    className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                                      fieldErrors.groupId ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    displayValue={() => selectedGroup?.name || ''}
                                    onChange={(event) => setGroupQuery(event.target.value)}
                                    placeholder="Select or search group..."
                                  />
                                  <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                  </Combobox.Button>
                                </div>
                                <Transition
                                  as={Fragment}
                                  leave="transition ease-in duration-100"
                                  leaveFrom="opacity-100"
                                  leaveTo="opacity-0"
                                  afterLeave={() => setGroupQuery('')}
                                >
                                  <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                    {filteredGroups.length === 0 && groupQuery !== '' ? (
                                      <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                        Nothing found.
                                      </div>
                                    ) : filteredGroups.length === 0 ? (
                                      <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                        No billing groups available.
                                      </div>
                                    ) : (
                                      filteredGroups.map((option) => (
                                        <Combobox.Option
                                          key={option.id}
                                          className={({ active }) =>
                                            `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                              active ? 'bg-blue-600 text-white' : 'text-gray-900'
                                            }`
                                          }
                                          value={option.id}
                                        >
                                          {({ selected, active }) => (
                                            <>
                                              <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                                {option.name}
                                              </span>
                                              {selected ? (
                                                <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-blue-600'}`}>
                                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                </span>
                                              ) : null}
                                            </>
                                          )}
                                        </Combobox.Option>
                                      ))
                                    )}
                                  </Combobox.Options>
                                </Transition>
                              </div>
                            </Combobox>
                            {fieldErrors.groupId && (
                              <p className="mt-1 text-sm text-red-600">{fieldErrors.groupId}</p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4 items-start">
                          <label className="text-sm font-medium text-gray-700 pt-2">
                            Type <span className="text-red-500">*</span>
                          </label>
                          <div className="col-span-2">
                            <Combobox
                              value={formData.type}
                              onChange={(value) => handleInputChange('type', value)}
                            >
                              <div className="relative">
                                <div className="relative w-full">
                                  <Combobox.Input
                                    className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                                      fieldErrors.type ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    displayValue={() => selectedType?.label || ''}
                                    onChange={(event) => setTypeQuery(event.target.value)}
                                    placeholder="Select or search type..."
                                  />
                                  <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                  </Combobox.Button>
                                </div>
                                <Transition
                                  as={Fragment}
                                  leave="transition ease-in duration-100"
                                  leaveFrom="opacity-100"
                                  leaveTo="opacity-0"
                                  afterLeave={() => setTypeQuery('')}
                                >
                                  <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                    {filteredTypes.length === 0 && typeQuery !== '' ? (
                                      <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                        Nothing found.
                                      </div>
                                    ) : (
                                      filteredTypes.map((option) => (
                                        <Combobox.Option
                                          key={option.value}
                                          className={({ active }) =>
                                            `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                              active ? 'bg-blue-600 text-white' : 'text-gray-900'
                                            }`
                                          }
                                          value={option.value}
                                        >
                                          {({ selected, active }) => (
                                            <>
                                              <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                                {option.label}
                                              </span>
                                              {selected ? (
                                                <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-blue-600'}`}>
                                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                </span>
                                              ) : null}
                                            </>
                                          )}
                                        </Combobox.Option>
                                      ))
                                    )}
                                  </Combobox.Options>
                                </Transition>
                              </div>
                            </Combobox>
                            {fieldErrors.type && (
                              <p className="mt-1 text-sm text-red-600">{fieldErrors.type}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Row 4: Time Spent, Worked Date - 2 columns */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                        <div className="grid grid-cols-3 gap-4 items-start">
                          <label className="text-sm font-medium text-gray-700 pt-2">
                            Time Spent (In Mins) <span className="text-red-500">*</span>
                          </label>
                          <div className="col-span-2">
                            <input
                              type="number"
                              min={TIME_SPENT_INCREMENT_MINUTES}
                              step={TIME_SPENT_INCREMENT_MINUTES}
                              value={formData.timeSpent}
                              onChange={(e) => handleInputChange('timeSpent', e.target.value)}
                              className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                                fieldErrors.timeSpent ? 'border-red-500' : 'border-gray-300'
                              }`}
                              placeholder="e.g. 15, 30, 45, 60"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              Enter time in 15-minute increments only
                            </p>
                            {fieldErrors.timeSpent && (
                              <p className="mt-1 text-sm text-red-600">{fieldErrors.timeSpent}</p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-4 items-start">
                          <label className="text-sm font-medium text-gray-700 pt-2">
                            Worked Date <span className="text-red-500">*</span>
                          </label>
                          <div className="col-span-2">
                            <DateTimePickerInput
                              value={formData.workedDate || ''}
                              onChange={(value) => handleInputChange('workedDate', value)}
                              max={getToday()}
                              showTimeSelect={false}
                              error={!!fieldErrors.workedDate}
                            />
                            {fieldErrors.workedDate && (
                              <p className="mt-1 text-sm text-red-600">{fieldErrors.workedDate}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Row 5: Description (full width) */}
                      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-start">
                        <label className="text-sm font-medium text-gray-700 md:col-span-1 pt-2">
                          Description <span className="text-red-500">*</span>
                        </label>
                        <div className="md:col-span-5">
                          <textarea
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            rows={4}
                            className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                              fieldErrors.description ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="Enter description of work performed"
                          />
                          {fieldErrors.description && (
                            <p className="mt-1 text-sm text-red-600">{fieldErrors.description}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="inline-flex justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        className="inline-flex justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default RecordTimeModal;
